
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const ClientManagementPage = () => {
  const features = [
    {
      icon: 'ri-user-heart-line',
      title: 'Smart Client Profiles',
      description: 'AI creates comprehensive client profiles with preferences, behavior patterns, and communication history.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Automated Follow-ups',
      description: 'Never miss a follow-up with intelligent scheduling and personalized communication sequences.'
    },
    {
      icon: 'ri-message-line',
      title: 'Communication Hub',
      description: 'Centralized communication across email, SMS, calls, and social media platforms.'
    },
    {
      icon: 'ri-file-list-line',
      title: 'Document Management',
      description: 'Organize and track all client documents, contracts, and transaction paperwork automatically.'
    },
    {
      icon: 'ri-notification-badge-line',
      title: 'Smart Reminders',
      description: 'AI-powered reminders for important dates, deadlines, and client milestones.'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Relationship Analytics',
      description: 'Track client satisfaction, engagement levels, and relationship strength over time.'
    }
  ];

  const workflowSteps = [
    {
      step: 1,
      title: 'Client Onboarding',
      description: 'Automated intake forms and profile creation',
      icon: 'ri-user-add-line',
      details: ['Digital intake forms', 'Preference questionnaires', 'Document collection', 'Initial consultation scheduling']
    },
    {
      step: 2,
      title: 'Needs Analysis',
      description: 'AI analyzes client requirements and preferences',
      icon: 'ri-search-eye-line',
      details: ['Budget analysis', 'Location preferences', 'Property type matching', 'Timeline assessment']
    },
    {
      step: 3,
      title: 'Personalized Service',
      description: 'Tailored communication and property recommendations',
      icon: 'ri-heart-line',
      details: ['Custom property alerts', 'Personalized market reports', 'Targeted communications', 'Preference-based showings']
    },
    {
      step: 4,
      title: 'Transaction Management',
      description: 'Streamlined process from offer to closing',
      icon: 'ri-file-check-line',
      details: ['Document tracking', 'Deadline management', 'Progress updates', 'Closing coordination']
    }
  ];

  const benefits = [
    {
      metric: '50%',
      label: 'More Referrals',
      description: 'Better client relationships lead to more referrals'
    },
    {
      metric: '75%',
      label: 'Time Saved',
      description: 'Automation reduces administrative work'
    },
    {
      metric: '90%',
      label: 'Client Satisfaction',
      description: 'Personalized service improves satisfaction'
    },
    {
      metric: '40%',
      label: 'Faster Closings',
      description: 'Streamlined processes accelerate transactions'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.8), rgba(37, 99, 235, 0.8)), url('https://public.readdy.ai/ai/img_res/73bd98b1b1e8baa391a8a3da04048908.jpg')`
        }}
      >
        <div className="container mx-auto px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              AI Client Management
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Build stronger relationships and deliver exceptional service with intelligent client management tools
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Try Free Demo
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Watch Overview
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Transform Your Client Relationships</h2>
            <p className="text-xl text-gray-600">See the impact of AI-powered client management</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl font-bold text-blue-600 mb-3">{benefit.metric}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{benefit.label}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Comprehensive Client Management</h2>
            <p className="text-xl text-gray-600">Everything you need to deliver exceptional client service</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Client Journey Workflow</h2>
            <p className="text-xl text-gray-600">From first contact to closing and beyond</p>
          </div>
          <div className="space-y-12">
            {workflowSteps.map((step, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="bg-white p-8 rounded-xl shadow-lg">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                        <i className={`${step.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <div className="text-sm text-blue-600 font-medium">Step {step.step}</div>
                        <h3 className="text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6">{step.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {step.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-center text-sm text-gray-700">
                          <i className="ri-check-line text-green-500 mr-2"></i>
                          {detail}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img 
                    src={`https://readdy.ai/api/search-image?query=real%20estate%20client%20management%20workflow%20step%20$%7Bstep.step%7D%2C%20professional%20service%20delivery%2C%20modern%20CRM%20interface%2C%20customer%20relationship%20building&width=600&height=400&seq=workflow${step.step}&orientation=landscape`}
                    alt={step.title}
                    className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Communication Hub */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Unified Communication Hub</h2>
            <p className="text-xl text-gray-600">All client communications in one place</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <i className="ri-mail-line text-3xl text-blue-600 mb-3"></i>
                <h4 className="font-bold text-gray-800">Email</h4>
                <p className="text-gray-600 text-sm">Automated sequences</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <i className="ri-message-line text-3xl text-green-600 mb-3"></i>
                <h4 className="font-bold text-gray-800">SMS</h4>
                <p className="text-gray-600 text-sm">Instant notifications</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <i className="ri-phone-line text-3xl text-purple-600 mb-3"></i>
                <h4 className="font-bold text-gray-800">Calls</h4>
                <p className="text-gray-600 text-sm">Call logging & tracking</p>
              </div>
              <div className="text-center p-6 bg-orange-50 rounded-lg">
                <i className="ri-share-line text-3xl text-orange-600 mb-3"></i>
                <h4 className="font-bold text-gray-800">Social</h4>
                <p className="text-gray-600 text-sm">Social media integration</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-bold mb-4 text-gray-800">Recent Client Interactions</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-user-line text-blue-600"></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">Sarah Johnson</div>
                      <div className="text-sm text-gray-600">Sent property recommendations</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">2 hours ago</div>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-user-line text-green-600"></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">Mike Chen</div>
                      <div className="text-sm text-gray-600">Scheduled showing appointment</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">1 day ago</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Seamless Integrations</h2>
            <p className="text-xl text-gray-600">Connect with your existing tools and platforms</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-database-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">CRM Systems</h3>
              <p className="text-gray-600 text-sm mt-2">Salesforce, HubSpot, Pipedrive</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">Email Platforms</h3>
              <p className="text-gray-600 text-sm mt-2">Gmail, Outlook, Mailchimp</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">Calendar Apps</h3>
              <p className="text-gray-600 text-sm mt-2">Google Calendar, Outlook, Calendly</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">Document Storage</h3>
              <p className="text-gray-600 text-sm mt-2">Google Drive, Dropbox, OneDrive</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Elevate Your Client Service Today</h2>
          <p className="text-xl mb-8 text-blue-100">
            Build stronger relationships and grow your business with AI-powered client management
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClientManagementPage;
